import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  clearStoredErpSession,
  normalizeErpRole,
  persistStoredErpSession,
  readStoredErpSession
} from '../lib/erpSessionStorage';

const AuthContext = createContext(null);
const ERP_MISSING_FUNCTION_CODES = ['42883', 'PGRST202'];
const formatRpcError = (prefix, error) => {
  const parts = [prefix];

  if (error?.code) {
    parts.push(`Kod: ${error.code}`);
  }

  if (error?.message) {
    parts.push(`Mesaj: ${error.message}`);
  }

  if (error?.hint) {
    parts.push(`Ipucu: ${error.hint}`);
  }

  return parts.join(' | ');
};

const toUserShape = (profile) => {
  if (!profile) return null;
  return {
    id: profile.id,
    email: profile.email,
    user_metadata: {
      full_name: profile.full_name || null,
      erp_role: normalizeErpRole(profile.role)
    }
  };
};

const toSessionShape = (profile) => {
  if (!profile) return null;
  return {
    erp_user_id: profile.id,
    email: profile.email,
    role: normalizeErpRole(profile.role),
    session_token: profile.session_token || null,
    session_expires_at: profile.session_expires_at || null
  };
};

const enrichProfileWithSession = (profile, storedSession = null) => {
  if (!profile) return null;

  return {
    ...profile,
    session_token: profile.session_token || storedSession?.session_token || null,
    session_expires_at: profile.session_expires_at || storedSession?.session_expires_at || null
  };
};

const getUserById = async (id) => {
  if (!id) return null;

  const { data, error } = await supabase
    .from('erp_users')
    .select('id, email, full_name, role, is_active, last_sign_in_at, auth_last_synced_at')
    .eq('id', id)
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

const getUserByEmail = async (email) => {
  if (!email) return null;

  const { data, error } = await supabase
    .from('erp_users')
    .select('id, email, full_name, role, is_active, last_sign_in_at, auth_last_synced_at')
    .eq('email', email.toLowerCase())
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

const getCurrentProfile = async () => {
  const { data, error } = await supabase.rpc('erp_whoami');

  if (error) {
    if (ERP_MISSING_FUNCTION_CODES.includes(error.code)) {
      throw new Error(formatRpcError('ERP session fonksiyonlari kullanilamiyor.', error));
    }
    throw error;
  }

  return Array.isArray(data) ? data[0] || null : data;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [erpProfile, setErpProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const applyProfile = (profile) => {
    if (!profile) {
      setUser(null);
      setSession(null);
      setErpProfile(null);
      return;
    }

    setErpProfile(profile);
    setUser(toUserShape(profile));
    setSession(toSessionShape(profile));
    persistStoredErpSession(profile);
  };

  const refreshProfile = async (targetUser = user) => {
    const storedSession = readStoredErpSession();

    if (storedSession?.session_token) {
      const found = await getCurrentProfile();
      if (!found || found.is_active === false) {
        applyProfile(null);
        clearStoredErpSession();
        throw new Error('Kullanici hesabi aktif degil. Lutfen yonetici ile iletisime gecin.');
      }

      const nextProfile = enrichProfileWithSession(found, storedSession);
      applyProfile(nextProfile);
      return nextProfile;
    }

    if (!targetUser?.id && !targetUser?.email) {
      applyProfile(null);
      return null;
    }

    const found = (await getUserById(targetUser.id)) || (await getUserByEmail(targetUser.email));
    if (!found || found.is_active === false) {
      applyProfile(null);
      clearStoredErpSession();
      throw new Error('Kullanici hesabi aktif degil. Lutfen yonetici ile iletisime gecin.');
    }

    applyProfile(found);
    return found;
  };

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      try {
        const stored = readStoredErpSession();
        if (!stored?.session_token) {
          if (stored?.erp_user_id || stored?.email) {
            clearStoredErpSession();
          }
          applyProfile(null);
          return;
        }

        const profile = await getCurrentProfile();
        if (!profile || profile.is_active === false) {
          clearStoredErpSession();
          applyProfile(null);
          return;
        }

        if (!mounted) return;
        applyProfile(enrichProfileWithSession(profile, stored));
      } catch {
        clearStoredErpSession();
        applyProfile(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void boot();

    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const normalizedEmail = email.trim().toLowerCase();
    const plainPassword = password || '';

    const { data, error } = await supabase.rpc('erp_login', {
      p_email: normalizedEmail,
      p_password: plainPassword
    });

    if (error) {
      if (ERP_MISSING_FUNCTION_CODES.includes(error.code)) {
        throw new Error(formatRpcError('ERP login RPC kullanilamiyor.', error));
      }
      throw error;
    }

    const matched = Array.isArray(data) ? data[0] : data;
    if (!matched) {
      throw new Error('Email veya sifre hatali.');
    }

    if (matched.is_active === false) {
      throw new Error('Bu kullanici pasif durumda.');
    }

    if (!matched.session_token) {
      throw new Error('ERP oturumu olusturulamadi. erp_fix_rls_sessions.sql scriptini calistirin.');
    }

    const nextProfile = {
      id: matched.id,
      email: matched.email,
      full_name: matched.full_name,
      role: matched.role,
      is_active: matched.is_active,
      last_sign_in_at: matched.last_sign_in_at,
      auth_last_synced_at: matched.auth_last_synced_at,
      session_token: matched.session_token,
      session_expires_at: matched.session_expires_at || null
    };

    applyProfile(nextProfile);
    navigate('/');

    return { user: toUserShape(nextProfile), session: toSessionShape(nextProfile) };
  };

  const register = async () => {
    throw new Error('Kayit olma devre disi. Kullanicilar sadece yonetici tarafindan acilir.');
  };

  const logout = async () => {
    try {
      if (session?.session_token) {
        await supabase.rpc('erp_logout');
      }
    } catch {
      // Session cleanup fallback is local storage clear below.
    }

    clearStoredErpSession();
    applyProfile(null);
    navigate('/login');
  };

  const value = useMemo(
    () => ({
      user,
      session,
      erpProfile,
      erpRole: normalizeErpRole(erpProfile?.role || session?.role || 'operator'),
      loading,
      login,
      register,
      logout,
      refreshProfile,
      isAuthenticated: !!session
    }),
    [user, session, erpProfile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
